import {
  Button,
  Option,
  SelectBox,
  SelectBoxProps,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { Client } from "@/types";

import { ClientsAPI, GetClientsQuery } from "../clients.api";
import CreateClientFlyout from "../Flyouts/CreateClientFlyout";

type ClientRecord = {
  client_id: number;
  uuid?: string;
  name: string;
  email?: string | null;
  organization?: string | null;
};

export type ClientSelectorProps = Omit<
  SelectBoxProps,
  | "data"
  | "renderOption"
  | "OptionTooltip"
  | "searchFn"
  | "onScroll"
  | "searchable"
  | "loading"
  | "defaultValue"
  | "value"
> & {
  defaultValue?: Option;
  value?: Option;
  onClientCreated?: (newClient: Client) => void;
  allowCreateNew?: boolean;
  query?: Omit<GetClientsQuery, "search" | "page" | "page_size">;
};

const ClientOptionComponent = ({ option }: { option: Partial<Option> }) => {
  const theme = useTheme();
  if (typeof option === "string") return <div>{option}</div>;
  const record = (option.data || {}) as ClientRecord;
  return (
    <div style={{ padding: "5px 0" }}>
      <div
        style={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "200px",
        }}
      >
        {record.name || option.label}
      </div>
      <div
        style={{
          fontSize: "12px",
          color: theme.palette.text.secondary,
          maxWidth: "200px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {record.organization || ""}
      </div>
    </div>
  );
};

export const ClientSelector: React.FC<ClientSelectorProps> = (props) => {
  const { query: defaultQuery } = props;
  const [search, setSearch] = useState("");
  const [showCreateClientFlyout, setShowCreateClientFlyout] = useState(false);

  const query: GetClientsQuery = {
    ...defaultQuery,
    search,
    page_size: 100,
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["clients", "list", query],
    queryFn: ({ pageParam = 1 }) =>
      ClientsAPI.get({
        ...query,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    getPreviousPageParam: (firstPage: any) => {
      if (firstPage.page - 1 === 0) return null;
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });

  const records: ClientRecord[] =
    data?.pages?.reduce<ClientRecord[]>(
      (acc, page) => [...acc, ...(page.data as ClientRecord[])],
      []
    ) || [];

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (isFetchingNextPage) return;
    if (!hasNextPage) return;
    fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback(
    (val: string) => setSearch(val || ""),
    300
  );

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <=
      clientHeight + 100 * ((data?.pages?.length as number) || 0)
    ) {
      debouncedFetchNextPage();
    }
  };

  const onClientCreated = (newClient: Client) => {
    refetch();
    props?.onClientCreated?.(newClient);
  };

  const actionComponent = props?.allowCreateNew ? (
    <Button
      size="xs"
      variant="subtle"
      color="primary"
      fullWidth
      style={{ marginBottom: 5 }}
      onClick={() => setShowCreateClientFlyout(true)}
    >
      + Create New Client
    </Button>
  ) : undefined;

  const options: Option[] = records.map((item) => ({
    label: item.name,
    value: item.client_id,
    data: item,
  }));

  return (
    <>
      <SelectBox
        placeholder="Select or search for a client..."
        label="Client"
        {...props}
        data={options}
        onScroll={handleScroll}
        searchable={true}
        searchFn={debouncedSearch}
        loading={isLoading}
        renderOption={(item) => {
          if (typeof item === "string") {
            return <div>{item}</div>;
          }
          return <ClientOptionComponent option={item} />;
        }}
        actionComponent={actionComponent}
      />
      <CreateClientFlyout
        open={showCreateClientFlyout}
        onSubmit={onClientCreated}
        handleClose={() => setShowCreateClientFlyout(false)}
      />
    </>
  );
};

export default ClientSelector;
